import { notification } from 'antd';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { NumberTypeEnum, useworkspaceAuthMeV2LazyQuery } from '@/types/generated/graphql';
import { LocalStorageKeys } from '@shared/constants';
import { parsePhoneNumber } from '@shared/utils/phone-number';
import { useDebounce } from '@shared/hooks/use-debounce';
import { useNavigate } from 'react-router-dom';
import { useAvailableNumberSearch } from '@modules/settings/my-numbers/purchase-number/hooks/use-number-search';
import { PurchaseNumberEdge } from '@modules/settings/my-numbers/purchase-number/purchase-number.types';
import { useClaimFreeNumbers } from './use-claim-free-numbers';
import { claimNumbersModalStore } from '../store/claim-numbers-modal.store';
import { buildClaimCartNumbers, ClaimNumberSelection } from '../utils/build-claim-cart-numbers';
import { claimNumbersSchema, ClaimNumbersSchema } from '../schema/claim-numbers.schema';
import { ClaimNumbersModalProps } from '../claim-numbers.types';
import { buildDefaultValues } from '../utils/build-default-values';

// US/CA local numbers are 10 digits — used to pad partial searches with wildcards (see handleSearch).
const NUMBER_LENGTH_MAX = 10;

export function useClaimNumbersForm({ availableRewardCount, prefill, onClaimed }: ClaimNumbersModalProps) {
  // `matchTo` = where the typed digits must appear in the number: 'any' | 'first' | 'last'.
  const [matchTo, setMatchTo] = useState('any');
  // The raw digits the user typed, kept separately so re-applying match-to doesn't need a re-type.
  const [searchedTerm, setSearchedTerm] = useState('');
  // Full number detail (price, addressRequirements, etc.) for each picked id, keyed by phone number.
  // The form field array only stores {phoneNumber, friendlyName}; this map preserves the rest for submit.
  const [selectedRows, setSelectedRows] = useState<Record<string, PurchaseNumberEdge>>({});
  // The actual search string sent to the API — the typed digits plus any wildcard padding.
  const [contains, setContains] = useState((prefill?.phoneNumber as string) ?? '');

  const navigate = useNavigate();
  const claimModal = claimNumbersModalStore();

  const numberClaimForm = useForm<ClaimNumbersSchema>({
    mode: 'onChange',
    defaultValues: buildDefaultValues(prefill),
    // the max-selectable rule depends on the reward cap, so the schema is built per render
    resolver: zodResolver(claimNumbersSchema(availableRewardCount)),
  });

  // One editable row per selected number (drives the friendly-name inputs); kept in sync in handleNumbersChange.
  const { fields, append, remove } = useFieldArray({
    control: numberClaimForm.control,
    name: 'numbers',
    shouldUnregister: true,
  });

  useEffect(() => {
    if (!prefill?.phoneNumber) return;
    numberClaimForm.reset(buildDefaultValues(prefill));
    setContains(prefill.phoneNumber);
    setSelectedRows((prev) => ({ ...prev, [prefill.phoneNumber as string]: prefill }));
  }, [prefill]);

  // Lazy re-fetch of the user/workspace after a successful claim, to refresh number counts.
  const workspaceAuthMeV2Result = useworkspaceAuthMeV2LazyQuery();

  // Wait 400ms after typing stops before hitting the search API.
  const debouncedContains = useDebounce(contains, 400);

  const selectedCapabilities = numberClaimForm.watch('capabilities');
  const selectedCountry = numberClaimForm.watch('isoCountry');

  // Turn the checkbox capabilities (['voice','SMS','MMS']) into the boolean flags the search query expects.
  function createGqlVariables() {
    const obj: { voiceEnabled?: boolean; smsEnabled?: boolean; mmsEnabled?: boolean } = {};

    (selectedCapabilities as string[])?.forEach((value) => {
      if (value === 'voice') obj.voiceEnabled = true;
      if (value === 'SMS') obj.smsEnabled = true;
      if (value === 'MMS') obj.mmsEnabled = true;
    });

    return obj;
  }

  const numberSearchResult = useAvailableNumberSearch({
    countryCode: selectedCountry,
    numberType: NumberTypeEnum.local,
    ...(debouncedContains && { contains: debouncedContains }),
    ...createGqlVariables(),
  });

  // Called on every keystroke in the number search box.
  const handleSearch = (inputText: string) => {
    // Normalize whatever was typed down to bare alphanumerics (strip +, spaces, dashes, etc.).
    const number = parsePhoneNumber(inputText) ?? {};
    const formattedTerm = (number || inputText)?.toString().replace(/[^A-Z0-9]/gi, '');
    // Too short to be meaningful — clear the filter so the API returns unfiltered results.
    if (formattedTerm.length < 2) {
      setContains('');
      return;
    }

    // Pad the term with '*' wildcards to 10 digits so the API can anchor the match:
    //   'first' → digits then wildcards (12*******), 'last' → wildcards then digits (*******12), 'any' → no padding.
    let result = formattedTerm;
    const searchTermLength = formattedTerm?.length ?? 0;
    const LengthDiff = NUMBER_LENGTH_MAX - searchTermLength;
    const repeatCount = searchTermLength >= NUMBER_LENGTH_MAX ? 0 : LengthDiff;
    const remaining = '*'.repeat(repeatCount);
    if (matchTo === 'first' && formattedTerm) result = formattedTerm + remaining;
    if (matchTo === 'last' && formattedTerm) result = remaining + formattedTerm;
    setSearchedTerm(formattedTerm);
    setContains(result);
  };

  // Called when the user chooses to claim later — remembers the choice so the modal isn't auto-opened again.
  // Also used for the modal's cross button and overlay click, since both should behave the same as claiming later.
  const onClaimLater = () => {
    localStorage.setItem(LocalStorageKeys.claimNumberSkipped, 'true');
    claimModal?.handleClose();
  };

  const { claim, loading: claimLoading } = useClaimFreeNumbers({
    onSuccess: (message) => {
      notification.success({ message });
      workspaceAuthMeV2Result?.[0]?.(); // refresh workspace/number counts
      claimModal?.handleClose();
      localStorage.setItem(LocalStorageKeys.claimNumberSkipped, 'true'); // also skip the modal for next refresh
      numberSearchResult?.refetch();
      onClaimed(); // clears the website-purchase prefill so a later reopen starts fresh
      navigate('/settings/my-numbers');
    },
    onError: (message) => notification.error({ message }),
  });

  // Runs after the schema validates. Reunites each selected number with its full detail row and fires the claim.
  const handleSubmit = (data: ClaimNumbersSchema) => {
    const selections: ClaimNumberSelection[] = data.numbers.map((entry) => ({
      phoneNumber: entry.phoneNumber,
      friendlyName: entry.friendlyName,
      // Prefer the cached row from selectedRows; fall back to the current search results if it's still there.
      row: (selectedRows[entry.phoneNumber] ??
        numberSearchResult?.phoneNumbers?.find((num) => num.id === entry.phoneNumber)?.row) as PurchaseNumberEdge,
    }));

    // Guard: a row can go missing if search results changed between selection and submit — the claim needs
    // numberType + addressRequirements, so bail rather than send an incomplete cart.
    const hasInvalid = selections.some(
      (selection) => selection.row?.numberType == undefined || selection.row?.addressRequirements == undefined
    );
    if (hasInvalid) {
      return notification?.warning({ message: 'Invalid form data.' });
    }

    claim(buildClaimCartNumbers(selections, (data.capabilities as string[]) ?? []));
  };

  // Fires when the user toggles the match-to (any/first/last) dropdown. Re-pads the *existing* typed term
  // with wildcards (same logic as handleSearch) so results update without the user retyping.
  const handleMatch = (align: string) => {
    numberClaimForm?.setValue('matchTo', align);
    let result = searchedTerm;
    const searchTermLength = searchedTerm?.length ?? 0;
    const LengthDiff = NUMBER_LENGTH_MAX - searchTermLength;
    const repeatCount = searchTermLength >= NUMBER_LENGTH_MAX ? 0 : LengthDiff;
    const remaining = '*'.repeat(repeatCount);
    if (align === 'first' && searchedTerm) result = searchedTerm + remaining;
    if (align === 'last' && searchedTerm) result = remaining + searchedTerm;
    setContains(result);
    setMatchTo(align);
  };

  // The Ant multi-select gives us the full list of currently-selected ids on every change.
  // We diff it against the form's field array to add/remove rows, and keep selectedRows in sync.
  const handleNumbersChange = (ids: string[]) => {
    const currentIds = fields.map((field) => field.phoneNumber);
    const newlyAddedIds = ids.filter((id) => !currentIds.includes(id));

    // append newly-selected numbers
    newlyAddedIds.forEach((id) => append({ phoneNumber: id, friendlyName: '' }));

    // remove de-selected numbers (iterate from the end to keep indexes valid)
    fields
      .map((field, index) => ({ field, index }))
      .filter(({ field }) => !ids.includes(field.phoneNumber))
      .sort((a, b) => b.index - a.index)
      .forEach(({ index }) => remove(index));

    // keep the detail map in sync with the current selection
    setSelectedRows((prev) => {
      const next: Record<string, PurchaseNumberEdge> = {};
      ids.forEach((id) => {
        const row =
          prev[id] ?? (numberSearchResult?.phoneNumbers?.find((num) => num.id === id)?.row as PurchaseNumberEdge);
        if (row) next[id] = row;
      });
      return next;
    });

    // Picking a number clears the search filter so the dropdown shows the full list again on next open,
    // instead of staying stuck on the last-typed filter.
    if (newlyAddedIds.length > 0) {
      setSearchedTerm('');
      setContains('');
    }
  };

  // Ids of everything currently selected — feeds back into the multi-select's `value`.
  const selectedIds = fields.map((field) => field.phoneNumber);
  // Top-level validation message for the numbers array (e.g. "Choose at least one", "at most N").
  const numbersError = numberClaimForm.formState.errors.numbers?.message;

  // Everything the modal component (ClaimNumbersModal) needs to render and drive the form.
  return {
    availableRewardCount,
    numberClaimForm,
    numberSearchResult,
    fields,
    selectedIds,
    numbersError,
    claimLoading,
    handleSearch,
    handleMatch,
    handleNumbersChange,
    handleSubmit,
    onClaimLater,
  };
}
